import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Spin,
  Empty,
  Card,
  Row,
  Col,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import useDebounce from "../../utils/useDebounce";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { createStyles } from "antd-style";

const useStyle = createStyles(({ css, token }) => {
  const { antCls } = token;
  return {
    customTable: css`
      ${antCls}-table {
        ${antCls}-table-container {
          ${antCls}-table-body,
          ${antCls}-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

const TableComponent = ({
  data,
  headers_table,
  loading,
  setSearch,
  setAction,
  totalPages,
  setPage,
  page,
  setItemsPerPage,
  itemsPerPage,
  setSortOrder,
  setSortColumn,
  deleting,
  isViewMode = false,
  showManageAction = false,
  hideEditButton = false,
  hideCreateButton = false,
  hideDeleteButton = false,
  customRenderers = {},
}) => {
  const { styles } = useStyle();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [generalSearch, setGeneralSearch] = useState("");
  const [sortInfo, setSortInfo] = useState({ column: null, order: null });

  const debouncedSearchText = useDebounce(searchText, 500);
  const debouncedGeneralSearch = useDebounce(generalSearch, 500);
  const screens = useBreakpoint();

  useEffect(() => {
    setSearch(debouncedGeneralSearch);
  }, [debouncedGeneralSearch, setSearch]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${headers_table[dataIndex] || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Resetear
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[debouncedSearchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleGeneralSearch = (e) => {
    setGeneralSearch(e.target.value);
  };

  const handlePageChange = (page, pageSize) => {
    setPage(page);
    setItemsPerPage(pageSize);
    setAction({ type: "reload" });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSortOrder(sorter.order);
      setSortColumn(sorter.columnKey);
      setSortInfo({ column: sorter.columnKey, order: sorter.order });
    } else {
      setSortOrder(null);
      setSortColumn(null);
      setSortInfo({ column: null, order: null });
    }
  };

  // Definimos un ancho fijo para cada columna normal
  const defaultColumnWidth = 150;
  const nonActionColumns = Object.keys(headers_table).map((key) => ({
    title: headers_table[key],
    dataIndex: key,
    key: key,
    width: defaultColumnWidth,
    sorter: (a, b) => {
      if (typeof a[key] === "string") {
        return a[key].localeCompare(b[key]);
      }
      return a[key] - b[key];
    },
    onCell: () => ({
      style: {
        minWidth: defaultColumnWidth,
        maxWidth: defaultColumnWidth,
      },
    }),
    ...getColumnSearchProps(key),
    ...(customRenderers[key] ? { render: customRenderers[key] } : {}),
  }));

  // Consolidamos todos los botones de acción en una única columna fija a la derecha
  const actionColumnWidth = 270; // Ajusta este ancho según la cantidad y tamaño de los botones
  const actionColumn = {
    title: "Acciones",
    key: "actions",
    fixed: "right",
    width: actionColumnWidth,
    render: (text, record) => (
      <Space size="small">
        {isViewMode ? (
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setAction({ type: "view", data: record })}
          >
            {screens.xs ? null : "Ver"}
          </Button>
        ) : (
          <>
            {showManageAction && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => setAction({ type: "manage", data: record })}
                type="text"
                disabled={!!deleting}
              >
                {screens.xs ? null : "Gestionar"}
              </Button>
            )}
            {!hideEditButton && (
              <Button
                icon={<EditOutlined />}
                onClick={() => setAction({ type: "edit", data: record })}
                type="text"
                disabled={!!deleting}
              >
                {screens.xs ? null : "Editar"}
              </Button>
            )}
            {!hideDeleteButton && (
              <Button
                icon={<DeleteOutlined />}
                onClick={() => setAction({ type: "delete", data: record })}
                type="text"
                danger
                loading={deleting === record.id}
                disabled={!!deleting && deleting !== record.id}
              >
                {screens.xs ? null : "Eliminar"}
              </Button>
            )}
          </>
        )}
      </Space>
    ),
  };

  // Combinamos las columnas normales y la de acciones
  const columns = [...nonActionColumns, actionColumn];

  // Se utiliza scroll en "max-content" para que la tabla se adapte al ancho total
  const EmptyText = () => <Empty description="Sin datos" />;

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card bordered={false} style={{ padding: 20 }}>
            <Space
              direction={screens.xs ? "vertical" : "horizontal"}
              style={{
                marginBottom: 20,
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Input
                placeholder="Buscar..."
                value={generalSearch}
                onChange={handleGeneralSearch}
                style={{ width: screens.xs ? "100%" : "300px" }}
                size="large"
                prefix={<SearchOutlined />}
              />
              <Space style={{ width: screens.xs ? "100%" : "auto" }} align="end">
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={() => setAction({ type: "reload" })}
                  disabled={!!deleting}
                  size="large"
                  block={screens.xs}
                >
                  Actualizar
                </Button>
                {!isViewMode && !hideCreateButton && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={() => setAction({ type: "create" })}
                    disabled={!!deleting}
                    block={screens.xs}
                  >
                    Agregar
                  </Button>
                )}
              </Space>
            </Space>
            <Spin spinning={loading}>
              <div className={styles.customTable}>
                <Table
                  dataSource={data}
                  columns={columns}
                  rowKey="id"
                  locale={{ emptyText: <EmptyText /> }}
                  pagination={{
                    current: page,
                    pageSize: itemsPerPage,
                    onChange: handlePageChange,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    total: totalPages * itemsPerPage,
                    size: "large",
                    responsive: true,
                  }}
                  onChange={handleTableChange}
                  size="middle"
                  expandable={{
                    expandedRowRender: (record) => (
                      <p style={{ margin: 0 }}>{record.description}</p>
                    ),
                    rowExpandable: (record) => !!record.description,
                  }}
                  scroll={{ x: "max-content" }}
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TableComponent;